import { friendlyErrorMessage, getLoginMethods, type LoginMethod } from '@nutians/shared';
import { EU_EEA_UK_COUNTRIES } from '@nutians/shared/schemas';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { type FormEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { getAccountCreationStatus, sendPhoneOtp, signIn, verifyPhoneOtp } from '../auth.js';
import { AuthShell, authButtonClass, authFieldClass } from '../components/AuthShell.js';
import Turnstile from '../components/Turnstile.js';

const METHODS = getLoginMethods();
const RESEND_COOLDOWN_SECONDS = 60;

function defaultCallingCode(): string {
  const locale = navigator.language ?? 'en-GB';
  const region = locale.split('-')[1]?.toUpperCase();
  return EU_EEA_UK_COUNTRIES.find((c) => c.isoCode === region)?.callingCode ?? '+44';
}

export default function LoginPage() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();

  const [activeMethod, setActiveMethod] = useState<LoginMethod>(METHODS[0]);
  const [accountCreationEnabled, setAccountCreationEnabled] = useState<boolean | null>(null);

  // Email form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailSubmitting, setEmailSubmitting] = useState(false);

  // Phone form state
  const [phoneStep, setPhoneStep] = useState<'phone' | 'otp'>('phone');
  const [callingCode, setCallingCode] = useState(defaultCallingCode);
  const [nationalNumber, setNationalNumber] = useState('');
  const [e164Phone, setE164Phone] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [phoneSubmitting, setPhoneSubmitting] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);
  const [cooldown, setCooldown] = useState(0);
  const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(
    () => () => {
      if (cooldownRef.current) clearInterval(cooldownRef.current);
    },
    [],
  );

  useEffect(() => {
    void getAccountCreationStatus().then(setAccountCreationEnabled);
  }, []);

  function startCooldown() {
    setCooldown(RESEND_COOLDOWN_SECONDS);
    cooldownRef.current = setInterval(() => {
      setCooldown((s) => {
        if (s <= 1) {
          if (cooldownRef.current) clearInterval(cooldownRef.current);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
  }

  async function onEmailSubmit(e: FormEvent) {
    e.preventDefault();
    setEmailError(null);
    setEmailSubmitting(true);
    const { error } = await signIn.email({ email, password });
    setEmailSubmitting(false);
    if (error) {
      setEmailError(friendlyErrorMessage(error, t('auth.signIn.error')));
      return;
    }
    navigate('/', { replace: true });
  }

  async function onSendCode(e: FormEvent) {
    e.preventDefault();
    setPhoneError(null);
    const raw = `${callingCode}${nationalNumber.replace(/\s/g, '')}`;
    const parsed = parsePhoneNumberFromString(raw);
    if (!parsed?.isValid()) {
      setPhoneError(t('auth.signIn.phone.invalidPhone'));
      return;
    }
    const phone = parsed.format('E.164');
    setPhoneSubmitting(true);
    const { error } = await sendPhoneOtp(phone, turnstileToken);
    setPhoneSubmitting(false);
    if (error) {
      setPhoneError(friendlyErrorMessage(error, t('auth.signIn.phone.sendFailed')));
      setTurnstileResetKey((k) => k + 1);
      return;
    }
    setE164Phone(phone);
    setPhoneStep('otp');
    startCooldown();
  }

  async function onVerifyCode(e: FormEvent) {
    e.preventDefault();
    setPhoneError(null);
    setPhoneSubmitting(true);
    const { error } = await verifyPhoneOtp(e164Phone, otpCode);
    setPhoneSubmitting(false);
    if (error) {
      setPhoneError(friendlyErrorMessage(error, t('auth.signIn.phone.verifyFailed')));
      setOtpCode('');
      return;
    }
    navigate('/', { replace: true });
  }

  async function handleResend() {
    if (cooldown > 0 || phoneSubmitting) return;
    setPhoneError(null);
    setPhoneSubmitting(true);
    const { error } = await sendPhoneOtp(e164Phone, turnstileToken);
    setPhoneSubmitting(false);
    if (error) {
      setPhoneError(friendlyErrorMessage(error, t('auth.signIn.phone.sendFailed')));
      return;
    }
    setOtpCode('');
    startCooldown();
  }

  return (
    <AuthShell
      title={t('auth.signIn.title')}
      subtitle={t('auth.signIn.subtitle')}
      footer={
        accountCreationEnabled === true ? (
          <>
            {t('auth.signIn.noAccount')}{' '}
            <Link to="/register" className="font-medium text-leaf hover:text-leaf-deep">
              {t('auth.signIn.createOne')}
            </Link>
          </>
        ) : accountCreationEnabled === false ? (
          <span role="status">{t('auth.register.temporarilyDisabled')}</span>
        ) : null
      }
    >
      {/* Method tabs */}
      <div role="tablist" className="mb-5 flex gap-1 rounded-lg bg-husk p-1">
        {METHODS.map((method) => (
          <button
            key={method}
            id={`signin-tab-${method}`}
            role="tab"
            aria-selected={activeMethod === method}
            aria-controls={`signin-panel-${method}`}
            type="button"
            onClick={() => setActiveMethod(method)}
            className={`flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition ${
              activeMethod === method
                ? 'bg-shell text-ink shadow-sm'
                : 'text-ink-soft hover:text-ink'
            }`}
          >
            {t(`auth.signIn.method.${method}`)}
          </button>
        ))}
      </div>

      {/* Email panel */}
      <div
        id="signin-panel-email"
        role="tabpanel"
        aria-labelledby="signin-tab-email"
        hidden={activeMethod !== 'email'}
      >
        <form onSubmit={(e) => void onEmailSubmit(e)} className="flex flex-col gap-4" noValidate>
          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-medium text-ink">{t('auth.signIn.email')}</span>
            <input
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={authFieldClass}
            />
          </label>
          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-medium text-ink">{t('auth.signIn.password')}</span>
            <input
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={authFieldClass}
            />
          </label>
          {emailError && (
            <p className="text-sm text-clay" role="alert">
              {emailError}
            </p>
          )}
          <button type="submit" disabled={emailSubmitting} className={`${authButtonClass} mt-1`}>
            {emailSubmitting ? t('auth.signIn.submitting') : t('auth.signIn.submit')}
          </button>
        </form>
      </div>

      {/* Phone panel */}
      <div
        id="signin-panel-phone"
        role="tabpanel"
        aria-labelledby="signin-tab-phone"
        hidden={activeMethod !== 'phone'}
      >
        {phoneStep === 'phone' ? (
          <form onSubmit={(e) => void onSendCode(e)} className="flex flex-col gap-4" noValidate>
            <div className="flex flex-col gap-1.5 text-sm">
              <label htmlFor="signin-calling-code" className="font-medium text-ink">
                {t('auth.signIn.phone.callingCodeLabel')}
              </label>
              <select
                id="signin-calling-code"
                value={callingCode}
                onChange={(e) => setCallingCode(e.target.value)}
                className={authFieldClass}
              >
                {EU_EEA_UK_COUNTRIES.map((c) => (
                  <option key={c.isoCode} value={c.callingCode}>
                    {c.name} ({c.callingCode})
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1.5 text-sm">
              <label htmlFor="signin-national-number" className="font-medium text-ink">
                {t('auth.signIn.phone.numberLabel')}
              </label>
              <input
                id="signin-national-number"
                type="tel"
                autoComplete="tel-national"
                inputMode="tel"
                placeholder="07911 123456"
                value={nationalNumber}
                onChange={(e) => setNationalNumber(e.target.value)}
                className={authFieldClass}
              />
            </div>
            <Turnstile
              onToken={setTurnstileToken}
              onError={() => setPhoneError(t('auth.signIn.phone.sendFailed'))}
              onExpire={() => setTurnstileToken('')}
              resetKey={turnstileResetKey}
            />
            {phoneError && (
              <p className="text-sm text-clay" role="alert" aria-live="assertive">
                {phoneError}
              </p>
            )}
            <button type="submit" disabled={phoneSubmitting} className={`${authButtonClass} mt-1`}>
              {phoneSubmitting ? t('auth.signIn.phone.sending') : t('auth.signIn.phone.sendCode')}
            </button>
          </form>
        ) : (
          <form onSubmit={(e) => void onVerifyCode(e)} className="flex flex-col gap-4" noValidate>
            <div className="flex flex-col gap-1.5 text-sm">
              <label htmlFor="signin-otp" className="font-medium text-ink">
                {t('auth.signIn.phone.codeLabel')}
              </label>
              <input
                id="signin-otp"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                placeholder="123456"
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className={`${authFieldClass} text-center font-mono tracking-widest`}
              />
            </div>
            {phoneError && (
              <p className="text-sm text-clay" role="alert" aria-live="assertive">
                {phoneError}
              </p>
            )}
            <button type="submit" disabled={phoneSubmitting} className={`${authButtonClass} mt-1`}>
              {phoneSubmitting ? t('auth.signIn.phone.verifying') : t('auth.signIn.phone.verify')}
            </button>
            <div aria-live="polite" className="text-center text-sm">
              {cooldown > 0 ? (
                <span className="text-ink-soft">
                  {t('auth.signIn.phone.resendIn', { seconds: cooldown })}
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => void handleResend()}
                  disabled={phoneSubmitting}
                  className="font-medium text-leaf hover:text-leaf-deep disabled:opacity-50"
                >
                  {t('auth.signIn.phone.resend')}
                </button>
              )}
            </div>
            <button
              type="button"
              onClick={() => {
                setPhoneStep('phone');
                setPhoneError(null);
              }}
              className="text-center text-sm text-ink-soft underline hover:text-ink"
            >
              {t('auth.signIn.phone.changeNumber')}
            </button>
          </form>
        )}
      </div>
    </AuthShell>
  );
}
